import { Typography } from "antd";
import React from "react";
import { PriceTier } from "../interface";
import { formatAmount, getPriceForDate } from "../pricing";

const { Text } = Typography;

const ReceiptLine: React.FC<{ label: React.ReactNode; amount: string; strong?: boolean }> = ({
  label,
  amount,
  strong,
}) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", padding: "4px 0" }}>
    <Text strong={strong} type={strong ? undefined : "secondary"}>
      {label}
    </Text>
    <Text strong={strong} style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
      {amount}
    </Text>
  </div>
);

interface Props {
  tiers: PriceTier[];
  selectedDate: Date | undefined;
  style?: React.CSSProperties;
}

/**
 * Receipt-style price breakdown for the selected date: base price, Thursday surcharge and total.
 */
const PriceReceipt: React.FC<Props> = ({ tiers, selectedDate, style }) => {
  const breakdown = selectedDate ? getPriceForDate(tiers, selectedDate) : undefined;

  return (
    <div
      style={{
        border: "1px dashed var(--neutral-alter)",
        backgroundColor: "var(--neutral-alt)",
        borderRadius: "6px",
        padding: "12px 16px",
        ...style,
      }}
    >
      {breakdown && selectedDate ? (
        <>
          <Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: "8px" }}>
            {selectedDate.toLocaleDateString("no", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}{" "}
            · uke {breakdown.week}
          </Text>
          <ReceiptLine label={`Bedriftspresentasjon (${breakdown.tier})`} amount={formatAmount(breakdown.base)} />
          {breakdown.thursdaySurcharge > 0 && (
            <ReceiptLine label="Torsdagstillegg" amount={formatAmount(breakdown.thursdaySurcharge)} />
          )}
          <div style={{ borderTop: "1px dashed var(--neutral-alter)", margin: "8px 0 4px" }} />
          <ReceiptLine label="Totalt" amount={formatAmount(breakdown.base + breakdown.thursdaySurcharge)} strong />
          <Text type="secondary" style={{ display: "block", fontSize: "12px", textAlign: "right" }}>
            Alle priser er ekskl. mva
          </Text>
        </>
      ) : (
        <Text type="secondary">
          {selectedDate ? "Pris avtales med Bindeleddet" : "Velg en dato i kalenderen for å se pris."}
        </Text>
      )}
    </div>
  );
};

export default PriceReceipt;
